/**
 * mail_write Command
 * 
 * Write or update email draft content - works like doc_write but for emails
 * Supports iterative, agentic composition of emails
 */

import * as Y from 'yjs';
import { SimpleDocCommand } from '../../../DocsApp/commands/v5/types';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../../../types/standardToolResults';
import { CommandExecutionContext } from '../../../../types/commands';
import useEntityStore from '../../../../stores/entityStore';
import type { MailDraftEntity, MailMessageEntity, EmailParticipant } from '../../types/entities';
import { parseEmailList } from '../../utils/emailHelpers';
import { mailCRDTService } from '../../services/crdt/mailCRDTService';

type WriteOperation = 
  | 'create'      // Create new draft
  | 'replace'     // Replace entire body
  | 'append'      // Add to end of body
  | 'prepend'     // Add to beginning of body
  | 'update';     // Update metadata (subject, recipients)

export const mailWriteCommand: SimpleDocCommand = {
  id: 'mail_write',
  logicalName: 'mail_write',
  version: 'v1',
  
  description: `Write or update email draft content - iterative composition like doc_write

**ITERATIVE EMAIL COMPOSITION** (like doc_write):
This command supports building emails incrementally, just like writing a document.

**OPERATIONS:**
- create: Start new draft
- replace: Replace entire body
- append: Add content to end
- prepend: Add content to beginning  
- update: Update subject/recipients

**BODY STORAGE:**
Email body is stored in Y.js (Y.XmlFragment 'body-editor') for real-time collaboration with the ProseMirror composer.
Each draft has a Y.Doc whose 'body-editor' XmlFragment syncs live with the open composer.

**EXAMPLES:**

Create new draft:
{
  "operation": "create",
  "integrationId": "integration_123",
  "to": "user@example.com",
  "subject": "Project Update",
  "body": "Hi there,\\n\\n"
}

Append to draft (iterative):
{
  "operation": "append", 
  "draftId": "draft_abc",
  "body": "Here are the latest updates:\\n- Item 1\\n- Item 2\\n\\n"
}

Continue building:
{
  "operation": "append",
  "draftId": "draft_abc",
  "body": "Let me know if you have questions.\\n\\nBest regards"
}

**CONTEXT-AWARE:**
When replying (inReplyTo provided), automatically:
- Sets subject to "Re: original subject"
- Populates recipients from original message
- Includes thread context`,
  
  schema: {
    parameters: [
      {
        name: 'operation',
        type: 'string',
        description: 'Operation type: create | replace | append | prepend | update',
        required: true,
        enum: ['create', 'replace', 'append', 'prepend', 'update'],
        validator: (value: string) => {
          const validOps = ['create', 'replace', 'append', 'prepend', 'update'];
          if (!validOps.includes(value)) {
            return `Invalid operation. Must be one of: ${validOps.join(', ')}`;
          }
          return null;
        }
      },
      {
        name: 'draftId',
        type: 'string',
        description: 'Draft entity ID (required for replace/append/prepend/update operations)',
        required: false,
        validator: (value: string, params: any) => {
          if (!value && params.operation !== 'create') {
            return `draftId is required for ${params.operation} operation`;
          }
          return null;
        }
      },
      {
        name: 'integrationId',
        type: 'string',
        description: 'Mail integration ID (required for create)',
        required: false,
        validator: (value: string, params: any) => {
          if (!value && params.operation === 'create' && !params.inReplyTo) {
            return 'integrationId is required for create operation';
          }
          return null;
        }
      },
      {
        name: 'to',
        type: 'string',
        description: 'Recipients (comma-separated emails or "Name <email>" format)',
        required: false,
        validator: (value: string, params: any) => {
          if (!value && params.operation === 'create' && !params.inReplyTo) {
            return 'to is required for create operation';
          }
          return null;
        }
      },
      {
        name: 'subject',
        type: 'string',
        description: 'Email subject',
        required: false,
        validator: (value: string, params: any) => {
          if (!value && params.operation === 'create' && !params.inReplyTo) {
            return 'subject is required for create operation';
          }
          return null;
        }
      },
      {
        name: 'body',
        type: 'string',
        description: 'Email body content (plain text)',
        required: false,
        validator: (value: string) => {
          if (value && value.length > 100000) {
            return 'Email body must be less than 100,000 characters';
          }
          return null;
        }
      },
      {
        name: 'cc',
        type: 'string',
        description: 'CC recipients (comma-separated)',
        required: false,
      },
      {
        name: 'bcc',
        type: 'string',
        description: 'BCC recipients (comma-separated)',
        required: false,
      },
      {
        name: 'inReplyTo',
        type: 'string',
        description: 'Message ID if replying (auto-populates recipients)',
        required: false,
      },
      {
        name: 'replyAll',
        type: 'boolean',
        description: 'Reply to all participants (only with inReplyTo)',
        required: false,
      },
    ],
  },
  
  examples: {
    good: [
      {
        input: {
          operation: 'create',
          integrationId: 'integration_123',
          to: 'john@example.com',
          subject: 'Project Update',
          body: 'Hi John,\n\n',
        },
        expectedOutput: createSuccessResult(
          'Draft created: Project Update',
          { 
            draftId: 'draft_abc', 
            wordCount: 2,
            operation: 'create',
            preview: 'Hi John,\n\n'
          }
        ),
        description: 'Create new draft with greeting',
      },
      {
        input: {
          operation: 'append',
          draftId: 'draft_abc',
          body: 'Here are the latest updates on the project:\n\n- Completed phase 1\n- Started phase 2\n\n',
        },
        expectedOutput: createSuccessResult(
          'Appended to draft: Project Update',
          { 
            draftId: 'draft_abc', 
            addedWords: 15,
            totalWords: 18,
            operation: 'append'
          }
        ),
        description: 'Append project updates',
      },
      {
        input: {
          operation: 'append',
          draftId: 'draft_abc',
          body: 'Let me know if you have any questions.\n\nBest regards,\nMe',
        },
        expectedOutput: createSuccessResult(
          'Appended to draft: Project Update',
          { 
            draftId: 'draft_abc', 
            addedWords: 10,
            totalWords: 28,
            operation: 'append'
          }
        ),
        description: 'Append closing',
      },
    ],
    bad: [
      {
        input: {
          operation: 'append',
          body: 'Some content'
        },
        error: 'draftId is required for append operation',
        why: 'Missing draftId for non-create operation',
        fix: 'Provide draftId parameter'
      },
      {
        input: {
          operation: 'create',
          subject: 'Test'
        },
        error: 'to is required for create operation',
        why: 'Missing required recipient',
        fix: 'Provide to parameter with email address'
      }
    ],
  },
  
  formatViewData: (result: any, toolInput: any) => {
    if (result.status === 'success') {
      return {
        type: 'mail_write',
        summary: result.message,
        data: result.data,
        operation: toolInput.operation
      };
    }
    return null;
  },
  
  handler: async (
    params: {
      operation: string;
      draftId?: string;
      integrationId?: string;
      to?: string;
      subject?: string;
      body?: string;
      cc?: string;
      bcc?: string;
      inReplyTo?: string;
      replyAll?: boolean;
    },
    context: CommandExecutionContext
  ): Promise<StandardToolResult> => {
    try {
      const { operation } = params;
      
      console.log('[MailApp:Write] Operation:', operation, params);
      
      // Validate operation
      const validOps = ['create', 'replace', 'append', 'prepend', 'update'];
      if (!validOps.includes(operation)) {
        return createErrorResult(
          'Invalid operation',
          `Operation must be one of: ${validOps.join(', ')}`,
          'validation'
        );
      }
      
      const entityState = useEntityStore.getState();
      
      // Handle CREATE operation
      if (operation === 'create') {
        return await handleCreate(params, entityState, context);
      }
      
      // All other operations need draftId
      if (!params.draftId) {
        return createErrorResult(
          'Missing draftId',
          `Operation "${operation}" requires draftId parameter`,
          'validation'
        );
      }
      
      // Find draft entity
      const draft = entityState.entities.find(
        (e: any) => (e._id === params.draftId || e.llmId === params.draftId) && e.entityType === 'Mail.Draft'
      ) as MailDraftEntity | undefined;
      
      if (!draft) {
        return createErrorResult(
          'Draft not found',
          `No draft found with ID: ${params.draftId}`,
          'validation'
        );
      }
      
      console.log('[MailApp:WriteDraft] Found draft:', draft.name);
      
      // Handle operations
      switch (operation) {
        case 'replace':
          return await handleReplace(draft, params, entityState);
        case 'append':
          return await handleAppend(draft, params, entityState);
        case 'prepend':
          return await handlePrepend(draft, params, entityState);
        case 'update':
          return await handleUpdate(draft, params, entityState);
        default:
          return createErrorResult('Unsupported operation', `Operation ${operation} not implemented`, 'validation');
      }
    } catch (error) {
      console.error('[MailApp:Write] ❌ Error:', error);
      return createErrorResult(
        'Write failed',
        error instanceof Error ? error.message : 'Unknown error',
        'execution'
      );
    }
  },
};

// ==================== Operation Handlers ====================

async function handleCreate(
  params: any,
  entityState: any,
  context: CommandExecutionContext
): Promise<StandardToolResult> {
  const { integrationId, to, subject, body = '', cc, bcc, inReplyTo, replyAll } = params;
  
  // Validate required params for create
  if (!integrationId) {
    return createErrorResult('Missing integrationId', 'integrationId is required for create operation', 'validation');
  }
  
  let toList: EmailParticipant[];
  let ccList: EmailParticipant[] | undefined;
  let bccList: EmailParticipant[] | undefined;
  let finalSubject = subject;
  let threadId: string | undefined;
  
  // If replying, auto-populate from original message
  if (inReplyTo) {
    console.log('[MailApp:Write] Creating reply draft for message:', inReplyTo);
    
    const originalMessage = entityState.entities.find(
      (e: any) => (e._id === inReplyTo || e.llmId === inReplyTo) && e.entityType === 'Mail.Message'
    ) as MailMessageEntity | undefined;
    
    if (!originalMessage) {
      return createErrorResult('Original message not found', `Cannot reply to non-existent message: ${inReplyTo}`, 'validation');
    }
    
    // Auto-populate recipients
    toList = replyAll 
      ? [...originalMessage.skeleton.from, ...originalMessage.skeleton.to.filter(p => 
          p.email !== (context as any).userEmail // Filter out self
        )]
      : originalMessage.skeleton.from;
    
    ccList = replyAll ? originalMessage.skeleton.cc : undefined;
    threadId = originalMessage.skeleton.threadId;
    
    // Auto-populate subject
    if (!finalSubject) {
      finalSubject = originalMessage.name.startsWith('Re: ') 
        ? originalMessage.name 
        : `Re: ${originalMessage.name}`;
    }
    
    console.log('[MailApp:Write] Reply context populated', { to: toList.length, subject: finalSubject });
  } else {
    // Regular compose - parse recipients
    if (!to) {
      return createErrorResult('Missing recipients', 'to is required for create operation', 'validation');
    }
    if (!subject) {
      return createErrorResult('Missing subject', 'subject is required for create operation', 'validation');
    }
    
    toList = parseEmailList(to);
    ccList = cc ? parseEmailList(cc) : undefined;
    bccList = bcc ? parseEmailList(bcc) : undefined;
    
    if (toList.length === 0) {
      return createErrorResult('Invalid recipients', 'No valid email addresses in "to" field', 'validation');
    }
  }
  
  // Create draft entity
  const draft = await entityState.actions.createEntity({
    entityType: 'Mail.Draft',
    name: finalSubject || 'Draft',
    spaceId: context.spaceId,
    skeleton: {
      '@type': 'Mail.Draft',
      integrationId,
      inReplyTo,
      threadId,
      to: toList,
      cc: ccList,
      bcc: bccList,
      bodyPlain: body,
      lastEditedAt: new Date().toISOString(),
      autoSaveVersion: 1,
    },
  });
  
  // Initialize Y.Doc body so it's immediately visible in an open composer
  if (body) {
    try {
      const { ydoc } = await mailCRDTService.getYDocForDraft(draft._id);
      const yxml = ydoc.getXmlFragment('body-editor');
      if (yxml.length === 0) {
        ydoc.transact(() => {
          yxml.insert(0, buildParagraphs(body));
        });
      }
    } catch (err) {
      console.warn('[MailApp:Write] Could not initialize Y.Doc for new draft:', err);
    }
  }
  
  const wordCount = body.split(/\s+/).filter((w: string) => w.length > 0).length;
  console.log('[MailApp:Write] ✅ Draft created:', draft._id, { wordCount });
  
  return createSuccessResult(
    `Draft created: ${finalSubject}`,
    {
      draftId: draft._id,
      llmId: draft.llmId,
      subject: finalSubject,
      to: toList.map(p => p.email),
      wordCount,
      preview: body.substring(0, 100),
    }
  );
}

/**
 * Build Y.XmlElement paragraph nodes from a plain-text string.
 * One paragraph per line, matching ProseMirror's paragraph schema.
 */
function buildParagraphs(text: string): Y.XmlElement[] {
  return text.split('\n').map(line => {
    const p = new Y.XmlElement('paragraph');
    if (line.length > 0) {
      const t = new Y.XmlText();
      t.insert(0, line);
      p.insert(0, [t]);
    }
    return p;
  });
}

/** Extract plain text from a Y.XmlFragment by walking its paragraph nodes. */
function xmlFragmentToText(yxml: Y.XmlFragment): string {
  const lines: string[] = [];
  for (let i = 0; i < yxml.length; i++) {
    const node = yxml.get(i);
    if (node instanceof Y.XmlElement || node instanceof Y.XmlFragment) {
      lines.push(node.toString().replace(/<[^>]+>/g, ''));
    } else if (node instanceof Y.XmlText) {
      lines.push(node.toString());
    }
  }
  return lines.join('\n');
}

async function handleReplace(
  draft: MailDraftEntity,
  params: any,
  entityState: any
): Promise<StandardToolResult> {
  const { body } = params;
  
  if (!body) {
    return createErrorResult('Missing body', 'body is required for replace operation', 'validation');
  }
  
  console.log('[MailApp:Write] Replacing draft body via Y.XmlFragment');
  
  const { ydoc } = await mailCRDTService.getYDocForDraft(draft._id);
  const yxml = ydoc.getXmlFragment('body-editor');
  
  ydoc.transact(() => {
    while (yxml.length > 0) yxml.delete(0, 1);
    yxml.insert(0, buildParagraphs(body));
  });
  
  const wordCount = body.split(/\s+/).filter((w: string) => w.length > 0).length;
  console.log('[MailApp:Write] ✅ Draft body replaced via Y.XmlFragment', { wordCount });
  
  return createSuccessResult(
    `Draft updated: ${draft.name}`,
    {
      draftId: draft._id,
      llmId: draft.llmId,
      operation: 'replace',
      wordCount,
      preview: body.substring(0, 100),
    }
  );
}

async function handleAppend(
  draft: MailDraftEntity,
  params: any,
  entityState: any
): Promise<StandardToolResult> {
  const { body } = params;
  
  if (!body) {
    return createErrorResult('Missing body', 'body is required for append operation', 'validation');
  }
  
  console.log('[MailApp:Write] Appending to draft via Y.XmlFragment');
  
  const { ydoc } = await mailCRDTService.getYDocForDraft(draft._id);
  const yxml = ydoc.getXmlFragment('body-editor');
  
  ydoc.transact(() => {
    yxml.insert(yxml.length, buildParagraphs(body));
  });
  
  const currentText = xmlFragmentToText(yxml);
  const wordCount = currentText.split(/\s+/).filter((w: string) => w.length > 0).length;
  const addedWords = body.split(/\s+/).filter((w: string) => w.length > 0).length;
  
  console.log('[MailApp:Write] ✅ Content appended via Y.XmlFragment', { addedWords, totalWords: wordCount });
  
  return createSuccessResult(
    `Appended to draft: ${draft.name}`,
    {
      draftId: draft._id,
      llmId: draft.llmId,
      operation: 'append',
      addedWords,
      totalWords: wordCount,
      preview: currentText.substring(Math.max(0, currentText.length - 150)),
    }
  );
}

async function handlePrepend(
  draft: MailDraftEntity,
  params: any,
  entityState: any
): Promise<StandardToolResult> {
  const { body } = params;
  
  if (!body) {
    return createErrorResult('Missing body', 'body is required for prepend operation', 'validation');
  }
  
  console.log('[MailApp:Write] Prepending to draft via Y.XmlFragment');
  
  const { ydoc } = await mailCRDTService.getYDocForDraft(draft._id);
  const yxml = ydoc.getXmlFragment('body-editor');
  
  ydoc.transact(() => {
    yxml.insert(0, buildParagraphs(body));
  });
  
  const currentText = xmlFragmentToText(yxml);
  const wordCount = currentText.split(/\s+/).filter((w: string) => w.length > 0).length;
  const addedWords = body.split(/\s+/).filter((w: string) => w.length > 0).length;
  
  console.log('[MailApp:Write] ✅ Content prepended via Y.XmlFragment', { addedWords, totalWords: wordCount });
  
  return createSuccessResult(
    `Prepended to draft: ${draft.name}`,
    {
      draftId: draft._id,
      llmId: draft.llmId,
      operation: 'prepend',
      addedWords,
      totalWords: wordCount,
      preview: currentText.substring(0, 150),
    }
  );
}

async function handleUpdate(
  draft: MailDraftEntity,
  params: any,
  entityState: any
): Promise<StandardToolResult> {
  const { subject, to, cc, bcc } = params;
  
  if (!subject && !to && !cc && !bcc) {
    return createErrorResult(
      'Nothing to update',
      'Provide at least one of: subject, to, cc, bcc',
      'validation'
    );
  }
  
  console.log('[MailApp:WriteDraft] Updating draft metadata');
  
  const updates: any = {
    skeleton: { ...draft.skeleton },
  };
  
  // Update subject (entity name)
  if (subject) {
    updates.name = subject;
    console.log('[MailApp:Write] Updated subject:', subject);
  }
  
  // Update recipients
  if (to) {
    updates.skeleton.to = parseEmailList(to);
    console.log('[MailApp:Write] Updated to:', updates.skeleton.to.length);
  }
  if (cc) {
    updates.skeleton.cc = parseEmailList(cc);
    console.log('[MailApp:Write] Updated cc:', updates.skeleton.cc.length);
  }
  if (bcc) {
    updates.skeleton.bcc = parseEmailList(bcc);
    console.log('[MailApp:Write] Updated bcc:', updates.skeleton.bcc.length);
  }
  
  updates.skeleton.lastEditedAt = new Date().toISOString();
  updates.skeleton.autoSaveVersion = draft.skeleton.autoSaveVersion + 1;
  
  await entityState.actions.updateEntity(draft._id, updates);
  
  console.log('[MailApp:Write] ✅ Metadata updated');
  
  return createSuccessResult(
    `Draft updated: ${subject || draft.name}`,
    {
      draftId: draft._id,
      llmId: draft.llmId,
      operation: 'update',
      subject: subject || draft.name,
      to: updates.skeleton.to?.map((p: EmailParticipant) => p.email),
    }
  );
}
